import { useCallback, useEffect, useRef, useState } from "react";
import { BookOpen, Download, Upload, ImagePlus, RotateCcw, ExternalLink, Loader2, X } from "lucide-react";
import { api, API_BASE, formatApiError, tokenStore } from "@/lib/api";
import { toast } from "sonner";
import { manualUrl } from "@/components/HelpButton";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

async function downloadAuth(path, filename) {
  const token = tokenStore.get();
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error("Download mislukt.");
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

export default function ManualBlock() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const mdRef = useRef();
  const imgRef = useRef();

  const load = useCallback(async () => {
    try { setData((await api.get("/manual")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response ? formatApiError(e.response?.data?.detail) : (e.message || "Er ging iets mis.")); }
    setBusy("");
  };

  const uploadMd = (file) => run("md", async () => {
    const fd = new FormData(); fd.append("file", file);
    await api.post("/admin/manual", fd, { headers: { "Content-Type": "multipart/form-data" } });
  }, `Handleiding bijgewerkt (${file.name}).`);

  const uploadImage = (file) => run("img", async () => {
    const fd = new FormData(); fd.append("file", file);
    await api.post("/admin/manual/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
  }, `Afbeelding ${file.name.toLowerCase()} opgeslagen.`);

  const resetMd = () => {
    if (!window.confirm("Eigen handleiding verwijderen en de standaardtekst terugzetten?")) return;
    run("reset", () => api.delete("/admin/manual"), "Standaardtekst hersteld.");
  };
  const resetImage = (name) => {
    if (!window.confirm(`Eigen upload van ${name} verwijderen en het origineel terugzetten?`)) return;
    run(`img-${name}`, () => api.delete(`/admin/manual/images/${name}`), `${name} hersteld.`);
  };

  const btn = "btn-brutal px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <section className="bg-card border-2 border-foreground heavy-shadow" data-testid="manual-admin-block">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight inline-flex items-center gap-2"><BookOpen className="w-5 h-5" /> Handleiding</h2>
          <p className="text-xs text-muted-foreground mt-1">Download de handleiding als Markdown, bewerk 'm offline en upload 'm weer. Koppen houden hun <code className="font-mono-num">{"{#anker}"}</code>; alles na <code className="font-mono-num [font-variant-ligatures:none]">{"<!-- ADMIN -->"}</code> zien alleen beheerders.</p>
        </div>
        <a href={manualUrl("start")} target="_blank" rel="noopener noreferrer" data-testid="manual-admin-open" className={`${btn} bg-primary text-primary-foreground`}>
          <ExternalLink className="w-3.5 h-3.5" /> Open handleiding
        </a>
      </div>
      {!data ? <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div> : (
        <div className="p-5 space-y-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs" data-testid="manual-admin-status">
            <span>Versie: <strong data-testid="manual-admin-source">{data.source === "custom" ? "Aangepast" : "Standaard"}</strong></span>
            {data.source === "custom" && <span>Bijgewerkt: <strong>{fmt(data.updated_at)}</strong>{data.updated_by ? ` door ${data.updated_by}` : ""}</span>}
            <span>Hoofdstukken: <strong>{(data.anchors || []).length}</strong></span>
            <span>Afbeeldingen: <strong>{(data.images || []).length}</strong>{data.custom_images?.length ? ` (${data.custom_images.length} eigen upload)` : ""}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => run("zip", () => downloadAuth("/admin/manual/export.zip", "handleiding.zip"))} disabled={!!busy} data-testid="manual-admin-download-zip" className={`${btn} bg-accent text-accent-foreground`}>
              {busy === "zip" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download .zip (md + afbeeldingen)
            </button>
            <button onClick={() => run("mdget", () => downloadAuth("/admin/manual/handleiding.md", "handleiding.md"))} disabled={!!busy} data-testid="manual-admin-download-md" className={`${btn} bg-background text-foreground`}>
              <Download className="w-3.5 h-3.5" /> Alleen .md
            </button>
            <input ref={mdRef} type="file" accept=".md,.markdown,.txt,text/markdown" className="hidden" data-testid="manual-admin-md-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMd(f); e.target.value = ""; }} />
            <button onClick={() => mdRef.current?.click()} disabled={!!busy} data-testid="manual-admin-upload-md" className={`${btn} bg-secondary text-secondary-foreground`}>
              {busy === "md" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload .md
            </button>
            <input ref={imgRef} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden" data-testid="manual-admin-img-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }} />
            <button onClick={() => imgRef.current?.click()} disabled={!!busy} data-testid="manual-admin-upload-img" className={`${btn} bg-background text-foreground`}>
              {busy === "img" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} Afbeelding uploaden
            </button>
            {data.source === "custom" && (
              <button onClick={resetMd} disabled={!!busy} data-testid="manual-admin-reset" className={`${btn} bg-destructive text-destructive-foreground`}>
                <RotateCcw className="w-3.5 h-3.5" /> Herstel standaardtekst
              </button>
            )}
          </div>

          <div>
            <div className="stat-label mb-2">Afbeeldingen (verwijs in de tekst met <code className="font-mono-num normal-case">images/naam.png</code>)</div>
            {data.images.length === 0 ? <div className="text-xs text-muted-foreground">Nog geen afbeeldingen.</div> : (
              <div className="flex flex-wrap gap-2" data-testid="manual-admin-images">
                {data.images.map((name) => {
                  const custom = (data.custom_images || []).includes(name);
                  return (
                    <span key={name} data-testid={`manual-img-${name}`} className={`inline-flex items-center gap-1.5 border-2 px-2 py-1 text-[11px] font-mono-num ${custom ? "border-primary bg-primary/10" : "border-foreground/30"}`}>
                      <a href={`${API_BASE}/manual/images/${name}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{name}</a>
                      {custom && <span className="stat-label text-primary">eigen</span>}
                      {custom && (
                        <button onClick={() => resetImage(name)} title="Origineel terugzetten" data-testid={`manual-img-reset-${name}`} className="text-muted-foreground hover:text-destructive">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
